import React, { useEffect } from 'react';

import { dataLayerProvider, tracker } from '@femtasy/helpers';
import classNames from 'classnames';
import { useIntl } from 'react-intl';

import AudioTeaser from 'components/AudioTeaser';
import Button from 'components/Button';
import Message from 'components/Message';
import MiniPlayer from 'components/MiniPlayer';
import TrialReminder from 'components/TrialReminder';
import Typography from 'components/Typography';

import { LOVERS_TRACKING, QUIZ_ROUTE } from 'containers/LoversQuiz/Steps';
import { useQuizContext } from 'containers/Quiz/hooks/useQuizContext';
import StepPageViewWrapper from 'containers/Quiz/StepPageViewWrapper';

import { isProduction } from 'helpers/env';

import { usePlayer } from 'hooks/usePlayer';

import { getResults, resultsMap } from './HandleResults';
import SpeakerMatch from './SpeakerMatch';

import './style.scss';

interface ResponseAnswer {
  tracking: string;
}

const LoversResults = () => {
  const { locale } = useIntl();

  const { currentlyPlaying } = usePlayer();
  const { responses } = useQuizContext();

  const genderAnswer = responses['gender']?.answer as ResponseAnswer;
  const toneAnswer = responses['tone']?.answer as ResponseAnswer;

  const results = getResults({
    primaryKey: genderAnswer.tracking,
    secondaryKey: toneAnswer.tracking,
    locale: locale.toLocaleLowerCase(),
    resultsMap,
  });

  useEffect(() => {
    tracker.sendEvent(
      LOVERS_TRACKING,
      [
        {
          section: 'results',
          speaker: results?.speaker,
        },
      ],
      !isProduction(),
      [dataLayerProvider],
    );
  }, [results?.speaker]);

  return (
    <StepPageViewWrapper trackingUrl={`${QUIZ_ROUTE}/results`}>
      <div data-testid="lovers-results" className="LoversResults">
        <div className="LoversResults__header">
          <div className="gradient" />
          <Typography variant="h1">
            <Message
              message={{ id: 'components.LoversQuiz.ResultsPage.title' }}
            />
          </Typography>

          <SpeakerMatch speaker={results?.speaker!!} />

          <Typography variant="h4">
            <Message
              message={{
                id: results?.adjectives!!,
              }}
            />
          </Typography>

          <Typography className="LoversResultd__header_info" variant="h4">
            <Message
              message={{
                id: results?.description!!,
              }}
            />
          </Typography>

          <Typography variant="small-thin">
            <Message
              message={{ id: 'components.LoversQuiz.ResultsPage.helpText' }}
            />
          </Typography>
        </div>
        <div className="LoversResults__audios">
          {results?.audios.map((audio) => (
            <AudioTeaser key={audio} storyId={audio} />
          ))}
        </div>

        <div className="LoversResults__trialReminder">
          <TrialReminder />
        </div>

        <div
          className={classNames('LoversResults__cta', {
            hasMiniplayer: currentlyPlaying,
          })}
        >
          <Button
            variant="secondary"
            title={{ id: 'components.loversQuiz.results.cta' }}
            linkTo="/sign-up"
          />
        </div>
        <MiniPlayer fixedToScreenBottom interactive={false} />
      </div>
    </StepPageViewWrapper>
  );
};

export default LoversResults;
